import { DOCUMENT } from '@angular/common'
import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core'
import { FormControl } from '@angular/forms'

import { delay } from '@app-lib/common.lib'

/**
 * # Range slider component
 *
 * Renders a range slider with any number of handles.
 *
 * -
 *
 * ## Input bindings
 *
 * | Input binding                                           | Default                  | Required |
 * | ------------------------------------------------------- | ------------------------ | -------- |
 * | {@link RangeSliderComponent.control [control]}          | `undefined`              | true     |
 * | {@link RangeSliderComponent.min [min]}                  | `0`                      | true     |
 * | {@link RangeSliderComponent.max [max]}                  | `100`                    | true     |
 * | {@link RangeSliderComponent.step [step]}                | `1`                      | true     |
 * | {@link RangeSliderComponent.connect [connect]}          | `undefined`              | false    |
 * | {@link RangeSliderComponent.labelFormat [labelFormat]}  | `(v) => v.toString()`    | false    |
 * | {@link RangeSliderComponent.direction [direction]}      | `ltr`                    | false    |
 * | {@link RangeSliderComponent.limit [limit]}              | `undefined`              | false    |
 * | {@link RangeSliderComponent.margin [margin]}            | `undefined`              | false    |
 * | {@link RangeSliderComponent.padding [padding]}          | `undefined`              | false    |
 *
 */
@Component({
    selector: 'app-range-slider',
    templateUrl: './range-slider.component.html',
    styleUrls: ['./range-slider.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
})
export class RangeSliderComponent implements OnChanges {
    /**
     *  The form control that the slider is connected to.
     */
    @Input()
    public control: FormControl

    /**
     *  Setting the minimum value of the slider.
     */
    @Input()
    public min: number = 0

    /**
     *  Setting the maximum value of the slider.
     */
    @Input()
    public max: number = 100

    /**
     *  Setting the step value of the slider.
     */
    @Input()
    public step: number = 1

    /**
     *  A boolean array that determines which handles are connected.
     *  The number of values in the array should be equal to the number of handles + 1
     *  @example
     *  // Renders the slider with two linked handles
     *  const control = new FormControl([20, 50])
     *  const connect = [false, true, false]
     */
    @Input()
    public connect: boolean[]

    @Input()
    public animate: boolean = true

    /**
     * A function that transforms the value of the slider into the tooltip string.
     */
    @Input()
    public labelFormat: (v: number) => string = (v) => v.toString()

    /**
     *  Setting the maximum distance between handles.
     */
    @Input()
    public limit: number

    /**
     *  Setting the minimum distance between handles.
     */
    @Input()
    public margin: number

    /**
     *  Setting the padding of the slider, preventing the user from selecting values that
     *  are less than the padding value away from either end of the slider
     */
    @Input()
    public padding: number | [number] | [number, number]

    /**
     *  Setting the direction of the slider.
     */
    @Input()
    public direction: 'ltr' | 'rtl' = 'ltr'

    @Output()
    public valueChanges = new EventEmitter()

    @Input()
    public showTooltip: boolean = true

    public tooltips: ({ to: (v: number) => string })[]

    constructor(
        @Inject(DOCUMENT) public readonly document: Document,
    ) {
    }

    /**
     * Setup tooltips whenever the control is changed
     * @param {SimpleChanges} changes
     */
    public ngOnChanges(changes: SimpleChanges) {
        if (changes.control || changes.labelFormat) {
            this.tooltips = (this.control.value as number[]).map(() => ({
                to: this.labelFormat,
            }))
        }

        if (changes.control?.currentValue instanceof FormControl) {
            void this.forceDisabled(changes.control.currentValue)
        }
    }

    /**
     * This is a hack to force the noUISlider to detect the disabled state if the form-control is
     * disabled at the time it is bound to the vendor component.
     */
    private async forceDisabled(control: FormControl): Promise<void> {
        if (control.disabled) {
            await delay(0)
            control.disable()
        }
    }
}
